import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Tile } from "components/UIComponents/Tile";
import { getDocumentSummary } from "api";
import { toast } from "react-toastify";
import styles from "./AISummaryModal.module.scss";
import { getFileIcon } from "./fileUtils";
import { HiOutlineSparkles } from "react-icons/hi2";
import { MdRefresh, MdContentCopy } from "react-icons/md";
import { IoSend } from "react-icons/io5";
import { DateFormat } from "../formatter";

const LoadingDots = ({style }) => (
  <span className={`${styles.loadingDots}`} style={style}>
    <span className={styles.loadingDot}></span>
    <span className={styles.loadingDot}></span>
    <span className={styles.loadingDot}></span>
  </span>
);

const AISummaryModal = ({ title, onClose, currentDoc, activityType, details }) => {
  const [promptInput, setPromptInput] = useState("");
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [hasInitialSummary, setHasInitialSummary] = useState(false);
  const conversationEndRef = useRef(null);

  const typeTextInConversation = useCallback((text, messageIndex) => {
    let index = 0;
    const isHtml = text.includes('<') && text.includes('>');
    
    const updateMessage = (content, isComplete = false) => {
      setConversationHistory(prev => 
        prev.map((msg, idx) => 
          idx === messageIndex 
            ? { ...msg, content, isTyping: !isComplete }
            : msg
        )
      );
    };

    if (isHtml) {
      const timer = setInterval(() => {
        if (index < text.length) {
          const currentText = text.substring(0, index + 1);
          updateMessage(currentText);
          index++;
        } else {
          updateMessage(text, true);
          setIsTyping(false);
          clearInterval(timer);
        }
      }, 10);
    } else {
      const timer = setInterval(() => {
        if (index < text.length) {
          updateMessage(text.substring(0, index + 1));
          index++;
        } else {
          updateMessage(text, true);
          setIsTyping(false);
          clearInterval(timer);
        }
      }, 20);
    }
  }, []);

  // Auto-scroll to bottom when new messages are added
  const scrollToBottom = () => {
    conversationEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory]);

  const fetchSummary = useCallback(async (regenerate = false, additionalPrompt = "") => {
    setIsTyping(true);
    
    // Add user question to conversation history if it's a follow-up
    if (additionalPrompt) {
      const userMessage = {
        type: 'user',
        content: additionalPrompt,
        timestamp: new Date()
      };
      setConversationHistory(prev => [...prev, userMessage]);
    }
    
    // Add AI response placeholder to conversation history
    const currentHistoryLength = conversationHistory.length + (additionalPrompt ? 1 : 0);
    const isFirstSummary = conversationHistory.filter(msg => msg.type === 'ai').length === 0;
    const aiMessage = {
      type: 'ai',
      content: isFirstSummary ? "Generating Summary..." : "...",
      timestamp: new Date(),
      isTyping: true
    };
    
    setConversationHistory(prev => [...prev, aiMessage]);
    
    try {
      const params = {};
      if (regenerate) {
        params.regenerate = 'true';
      }
      if (additionalPrompt) {
        params.additionalPrompt = additionalPrompt;
      }
      
      const response = await getDocumentSummary(currentDoc?.documentId, params);
      let summaryText = response?.data?.data?.summary || "";
      
      setTimeout(() => {
        const finalText = summaryText || "No summary available.";
        typeTextInConversation(finalText, currentHistoryLength);
        if (!hasInitialSummary) {
          setHasInitialSummary(true);
        }
      }, 1500);
      
    } catch (error) {
      console.error("Failed to fetch AI summary", error);
      const errorText = "Failed to generate summary. Please try again later.";
      
      setTimeout(() => {
        typeTextInConversation(errorText, currentHistoryLength);
      }, 1500);
    }
  }, [currentDoc?.documentId, conversationHistory.length, hasInitialSummary, typeTextInConversation]);

  useEffect(() => {
    if (currentDoc?.documentId && conversationHistory.length === 0) {
      fetchSummary();
    }
  }, [currentDoc?.documentId, fetchSummary, conversationHistory.length]);

  const handleRegenerate = () => {
    fetchSummary(true);
  };

  const handleCopyToClipboard = async () => {
    try {
      // Get the latest AI message content
      const latestAiMessage = conversationHistory
        .filter(msg => msg.type === 'ai')
        .pop();
      
      if (latestAiMessage) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = latestAiMessage.content;
        const textContent = tempDiv.textContent || tempDiv.innerText || latestAiMessage.content;
        
        await navigator.clipboard.writeText(textContent);
        toast.success("Summary copied to clipboard!");
      }
    } catch (error) {
      console.error("Failed to copy to clipboard", error);
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleSendPrompt = () => {
    if (promptInput.trim()) {
      fetchSummary(true, promptInput.trim());
      setPromptInput("");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendPrompt();
    }
  };

  return (
    <Dialog
      title={
        <div className="d-flex align-items-center gap-2">
          <div className={`d-flex justify-content-center align-items-center ${styles.aiIconWrapper}`}>
            <HiOutlineSparkles className={styles.aiIcon} />
          </div>
          <span>Linq AI | Document Summary</span>
        </div>
      }
      onClose={onClose}
      className="dialog-sm"
      width="600px"
    >
      <div className={styles.modalContent}>
        {/* Document Header Section */}
        <div className="d-flex justify-content-between align-items-start px-3">
          {/* Left Side - File Name with Icon */}
          <div className="d-flex align-items-center gap-2" style={{ flex: 1, minWidth: 0 }}>
            <img
              src={getFileIcon(currentDoc?.documentName || currentDoc?.name)}
              alt="File Icon"
              width="20"
              height="20"
              style={{ flexShrink: 0 }}
            />
            <span 
              className="fw-bold text-dark" 
              style={{ 
                fontSize: "14px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
              title={currentDoc?.documentName || currentDoc?.name || "Document"}
            >
              {currentDoc?.documentName || currentDoc?.name || "Document"}
            </span>
          </div>
          
          {/* Right Side - Date, Activity Type and Details */}
          <div className="text-muted text-end" style={{ flexShrink: 0, marginLeft: "16px" }}>
            <div>
              <span style={{ textDecoration: "underline" }}>
                {DateFormat(currentDoc?.dateCreated)}
              </span>
            </div>
            {details && (
              <div className="mt-1 small">
                {details}
              </div>
            )}
          </div>
        </div>

        <hr className={styles.customHr} />

        {/* Conversation History */}
        <div className={styles.conversationWrapper}>
          <div className={styles.conversationContainer}>
            {conversationHistory.map((message, index) => (
              <div key={index} className={`mb-3 ${styles.messageContainer}`}>
                {message.type === 'user' ? (
                  <div className={`${styles.userMessage} text-end`}>
                    <div className={`${styles.userBubble} text-muted`}>
                      {message.content}
                    </div>
                  </div>
                ) : (
                  <div className={styles.aiMessage}>
                    <div className={styles.aiBubble}>
                      <div
                        className="text-dark"
                        style={{
                          fontSize: "14px",
                          lineHeight: "1.6",
                        }}
                      >
                        {message.content.includes('<') && message.content.includes('>') ? (
                          <div>
                            <div dangerouslySetInnerHTML={{ __html: message.content }} />
                            {message.isTyping && <span className={styles.cursor}>|</span>}
                          </div>
                        ) : message.content === "..." ? (
                          <LoadingDots />
                        ) : message.content.startsWith("Generating Summary") ? (
                          <span>
                            Generating Summary
                            <LoadingDots style={{ marginLeft: "4px" }} />
                          </span>
                        ) : (
                          <span>
                            {message.content}
                            {message.isTyping && <span className={styles.cursor}>|</span>}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {conversationHistory.length === 0 && (
              <div className="text-center text-muted">
                <p>Generating initial summary...</p>
              </div>
            )}
            <div ref={conversationEndRef} />
          </div>
        </div>

        {/* Action Section - Always show but disabled initially */}
        <div className={`mt-3 ${styles.actionSection}`}>
          <div className="d-flex align-items-center gap-3">
            {/* Left Side - Action Buttons */}
            <div className="d-flex gap-2">
              <Button
                className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                onClick={handleRegenerate}
                disabled={isTyping || !hasInitialSummary}
                title="Regenerate"
                style={{ width: "32px", height: "32px" }}
              >
                <MdRefresh size={16} />
              </Button>
              <Button
                className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                onClick={handleCopyToClipboard}
                disabled={!hasInitialSummary}
                title="Copy to clipboard"
                style={{ width: "32px", height: "32px" }}
              >
                <MdContentCopy size={16} />
              </Button>
            </div>

            {/* Center - Input and Send */}
            <div className="d-flex flex-grow-1 gap-2">
              <input
                type="text"
                className="form-control"
                placeholder="Ask follow-up questions or request changes..."
                value={promptInput}
                onChange={(e) => setPromptInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isTyping || !hasInitialSummary}
                style={{ height: "32px", fontSize: "13px" }}
              />
              <Button
                className="common-btn bgBtnPrimary d-flex align-items-center justify-content-center"
                onClick={handleSendPrompt}
                disabled={!promptInput.trim() || isTyping || !hasInitialSummary}
                style={{ width: "32px", height: "32px", minWidth: "32px" }}
              >
                <IoSend size={14} style={{ marginBottom: "3px" }} />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="dialog-footer">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="ms-2"></div>
          <small className="text-muted ms-5" style={{ fontSize: "12px" }}>
            Generated by Linq AI. May contain errors.
          </small>
          <Button className="common-btn bgBtnSecondary me-4" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default AISummaryModal;